import cx from 'classnames';
import type { FC, ReactNode } from 'react';

import { Color, type IconName } from '@zen/Styleguide';

import BarChart, { type BarType } from '../BarChart';
import IconCircle from '../IconCircle';
import { chartHeightConfig, clampPercentage, getProgressBarLabel, progressLineHeightClassNames } from './helpers';

export type ProgressType = 'info';
interface Props {
  className?: string;
  icon?: IconName;
  label?: string;
  percentage: number;
  showCompleted?: boolean;
  showLabel?: boolean;
  variant?: 'compact' | 'default';
}

const ProgressBar: FC<Props> = (props) => {
  const { className, icon, percentage = 0, label = '', showCompleted = false, showLabel = true, variant = 'default' } = props;
  const clampedPercentage = clampPercentage(percentage);
  const progressBarLabel = getProgressBarLabel(label, clampedPercentage);

  const isCompleted: boolean = showCompleted && clampedPercentage === 100;
  const barType: BarType = isCompleted ? 'completed' : 'info';

  const iconContainerClassName: string = cx('absolute top-[50%] flex items-center justify-center', chartHeightConfig[variant]);
  const barChartClassNames: string = cx('relative flex w-full items-center', chartHeightConfig[variant]);
  const progressLineClassNames: string = cx(
    progressLineHeightClassNames[variant],
    'rounded-full bg-grey-lighter',
    { 'min-w-0 w-full': Boolean(icon), 'leading-10': !icon },
    className
  );

  const chartSegment: ReactNode = (
    <BarChart className={progressLineClassNames} configs={[{ percentage: clampedPercentage, type: barType }]} />
  );

  const progressBarIcon: ReactNode = icon ? (
    <div
      className={iconContainerClassName}
      data-testid="progress-bar-icon"
      style={{ left: `${clampedPercentage}%`, transform: 'translate(-50%, -50%)' }}
    >
      <IconCircle color={Color.AZURE_LIGHTER} icon={icon} iconClassName="text-azure-base" showBorder variant={variant} />
    </div>
  ) : null;

  const renderBarChart = (): ReactNode => {
    if (icon) {
      return (
        <div className={barChartClassNames}>
          {chartSegment}
          {progressBarIcon}
        </div>
      );
    }
    return chartSegment;
  };

  return (
    <div data-component="progress-bar" data-testid="progress-bar">
      {showLabel && (
        <div className="text-xs text-grey-dark font-bold leading-normal mb-1" data-testid="progress-bar-label">
          {progressBarLabel}
        </div>
      )}
      {renderBarChart()}
    </div>
  );
};

export type { Props as ProgressBarProps };

export default ProgressBar;
